import { Collapse } from '@mui/material';
import CustomTab from '@src/components/CustomTab/CustomTab';
import PageHeaderMobile from '@src/components/PageHeader/PageHeaderMobile';
import SearchBar from '@src/components/SearchBar';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import GeneralSettings from './General';
import BranchSettings from './BranchSettings';
import { CustomAlertType } from '../Products';
import { RESTAURANT } from '@src/Constants';
import { useAppSelector } from '@src/store/hooks';
import TeamsAndPermission from './TeamsAndPermission';
import ApprovalList from './integrations/Tally/ApprovalList';
import Integrations from './integrations';

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
`;

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#order-details {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 16px;
		flex-shrink: 0;
		.field {
			display: flex;
			gap: 6px;
			color: #5c5c5c;
			font-size: 14px;
			font-weight: 500;
			span {
				font-weight: 700;
			}
		}
	}
	#filter-container {
		display: flex;
		gap: 10px;
	}
`;

const ProductContainer = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
});

interface Props {
	collapse: any;
	setCollapse: any;
	setSearchValue: any;
	searchValue: any;
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
	view: any;
	setView: any;
	integrationsView: any;
	setIntegrationsView: any;
}

const SettingsMobileHomePage: React.FC<Props> = ({
	collapse,
	setCollapse,
	setSearchValue,
	searchValue,
	setAlert,
	displayCustomError,
	view,
	setView,
	integrationsView,
	setIntegrationsView,
}) => {
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;
	const [tabValue, setTabValue] = useState('1');
	const [rows, setRows] = useState([]);
	const [filteredRows, setFilteredRows] = useState([]);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const { pathname } = useLocation();
	const { id } = useParams();

	useEffect(() => {
		if (pathname === `/restaurants/${id}/settings/branch`) {
			setTabValue('2');
		}
	}, [pathname]);

	const tabs = isRestaurant
		? [
				{
					label: 'General Settings',
					value: '1',
					content: <GeneralSettings setAlert={setAlert} displayCustomError={displayCustomError} />,
				},
				{
					label: isRestaurant ? 'Dealer Settings' : 'Supplier Settings',
					value: '2',
					content: (
						<BranchSettings
							setAlert={setAlert}
							displayCustomError={displayCustomError}
							setSearchValue={setSearchValue}
							searchValue={searchValue}
							rows={rows}
							setRows={setRows}
							filteredRows={filteredRows}
							setFilteredRows={setFilteredRows}
						/>
					),
				},
				{
					label: 'Team and Permissions',
					value: '3',
					content: (
						<TeamsAndPermission
							setAlert={setAlert}
							displayCustomError={displayCustomError}
							view={view}
							setView={setView}
							setSearchValue={setSearchValue}
							searchValue={searchValue}
						/>
					),
				},
				{
					label: 'Integrations',
					value: '4',
					content: (
						<Integrations
							view={integrationsView}
							setView={setIntegrationsView}
							setAlert={setAlert}
							displayCustomError={displayCustomError}
						/>
					),
				},
		  ]
		: [
				{
					label: 'General Settings',
					value: '1',
					content: <GeneralSettings setAlert={setAlert} displayCustomError={displayCustomError} />,
				},
				{
					label: isRestaurant ? 'Dealer Settings' : 'Supplier Settings',
					value: '2',
					content: (
						<BranchSettings
							setAlert={setAlert}
							displayCustomError={displayCustomError}
							setSearchValue={setSearchValue}
							searchValue={searchValue}
							rows={rows}
							setRows={setRows}
							filteredRows={filteredRows}
							setFilteredRows={setFilteredRows}
						/>
					),
				},
				{
					label: 'Team and Permissions',
					value: '3',
					content: (
						<TeamsAndPermission
							setAlert={setAlert}
							displayCustomError={displayCustomError}
							view={view}
							setView={setView}
							setSearchValue={setSearchValue}
							searchValue={searchValue}
						/>
					),
				},
				{
					label: 'Restaurant Approval List',
					value: '4',
					content: (
						<ApprovalList
							setAlert={setAlert}
							displayCustomError={displayCustomError}
							rows={rows}
							setRows={setRows}
							filteredRows={filteredRows}
							setFilteredRows={setFilteredRows}
						/>
					),
				},
				{
					label: 'Integrations',
					value: '5',
					content: (
						<Integrations
							view={integrationsView}
							setView={setIntegrationsView}
							setAlert={setAlert}
							displayCustomError={displayCustomError}
						/>
					),
				},
		  ];

	const navigate = useNavigate();
	const handleSearch = (value: any) => {
		setSearchValue(value);
	};

	const handleFrontEndSearch = (value: any) => {
		setSearchValue(value);
		const filteredRows: any = rows?.filter((row: any) => {
			const lowerCaseValue = value?.toLowerCase();

			return (
				row?.name?.toLowerCase()?.includes(lowerCaseValue) ||
				row?.id?.toString()?.includes(lowerCaseValue) ||
				row?.address?.toString()?.includes(lowerCaseValue)
			);
		});
		setFilteredRows(filteredRows);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
	};

	const handleArrowBack = () => {
		// const value = Number(tabValue);
		// if (value > 1) {
		// 	setTabValue(String(value - 1));
		// 	return;
		// }
		const value = isRestaurant ? '4' : '5';
		if (tabValue == '3' && view == 'permission') {
			setView('team');
			return;
		} else if (tabValue === value && integrationsView === 'manageTally') {
			setIntegrationsView('list');
			return;
		}
		navigate(-1);
	};

	return (
		<StyledMobileContainer>
			<PageHeaderMobile
				title="Settings"
				setCollapse={setCollapse}
				collapse={collapse}
				onClick={handleArrowBack}
				hideSearch={
					isRestaurant
						? ['1', '4']?.includes(tabValue)
						: ['1', '5']?.includes(tabValue) ||
						  integrationsView === 'manageTally' ||
						  view === 'permission'
				}
			/>
			{collapse ? (
				<Collapse
					in={collapse}
					style={{
						display: 'flex',
						gap: '15px',
						alignItems: 'center',
						flexShrink: 0,
						padding: '16px',
					}}
				>
					<SearchBar
						style={{
							width: '328px',
							height: '36px',
						}}
						value={searchValue}
						onChange={tabValue === '3' ? handleSearch : handleFrontEndSearch}
						isLoading={false}
						autoFocus
					/>
				</Collapse>
			) : null}
			<CustomTab
				sx={{
					flexShrink: 0,
					overflowY: 'hidden',
				}}
				tabValue={tabValue}
				handleTabChange={handleTabChange}
				tabs={tabs}
			/>
		</StyledMobileContainer>
	);
};

export default SettingsMobileHomePage;
